<template>
  <form autocomplete="on" class="companion-login-form form-group">
    <div class="mx-12">
      <base-input
        class="companion-login-input"
        :label="$t('LoginPage.loginLabel')"
        :placeholder="$t('LoginPage.loginPlaceholder')"
        v-model="$v.login.$model"
        @animationstart="loginAnimationStart"
        @focusout="updateLoginInputErrors"
        @input="resetLoginInputErrors"
        :any-error="loginErrors.length > 0"
      />
      <input-error :errors="loginErrors" />
      <password-input
        class="companion-password-input"
        v-model="$v.password.$model"
        :label="$t('LoginPage.password')"
        :placeholder="$t('LoginPage.passwordPlaceholder')"
        @animationstart="passwordAnimationStart"
      />
      <base-checkbox
        check-box-classes="border-gray-300"
        :value="stayLoggedIn"
        @change="onChangeStayLoggedIn"
        :disabled="!cookiesAccepted"
      >
        <span class="form-input-label">
          {{ $t("LoginPage.keepMeLoggedIn") }}
        </span>
        <svg-icon
          name="info-circle"
          class="icon-sm text-gray-500 ms-2"
          v-tooltip="keepMeLoggedInTooltip"
        />
      </base-checkbox>
      <base-button
        :isButtonType="false"
        class="mx-16 mt-6 flex items-center justify-center"
      >
        <input
          type="submit"
          class="btn btn-primary text-white bg-blue-500 h-full w-full"
          @click.prevent="doLogin"
          :value="$t('LoginPage.loginButton')"
          :disabled="isLoginButtonDisabled"
        />
      </base-button>
    </div>
  </form>
</template>

<script>
import PasswordInput from "@/components/ui/PasswordInput.vue";
import BaseInput from "@/components/ui/BaseInput.vue";
import InputError from "@/components/ui/InputError.vue";
import BaseCheckbox from "@/components/ui/BaseCheckbox.vue";
import AuthOptions from "@/models/AuthOptions.model.js";
import { login } from "@/services/auth.js";
import { email, required } from "vuelidate/lib/validators";
import { getErrorMessageFrom } from "@/services/api.js";
import { updateUserAccessRights } from "@/config/ability.js";
import { mapActions, mapState } from "vuex";
import { areCookiesAccepted } from "@/utils/cookies";
import {
  getLocalStorageStayLoggedIn,
  setLocalStorageStayLoggedIn,
} from "@/store/localStorage";

export default {
  beforeMount() {
    AuthOptions.get();
  },
  components: {
    BaseCheckbox,
    PasswordInput,
    BaseInput,
    InputError,
  },
  data() {
    return {
      login: "",
      password: "",
      isLoading: false,
      loginErrors: [],
      // Indicates if the username was filled by autofill
      loginAutofill: false,
      // Indicates if the password was filled by autofill
      passwordAutofill: false,
      loginAttemptsCount: 0,
      loggingTimeoutIds: -1,
      stayLoggedIn: false,
    };
  },
  validations: {
    login: {
      required,
      login(value) {
        return /^[a-zA-Z0-9_-]*$/.test(value) || email(value);
      },
    },
    password: {
      required,
    },
  },
  computed: {
    ...mapState("Auth", ["cookiesAccepted"]),
    lockoutOptions() {
      return AuthOptions.query().first()?.lockout;
    },
    isLoginButtonDisabled() {
      // The login button should be disabled if there is no username and password and if both values were not autofilled
      return (
        ((!this.login && !this.loginAutofill) ||
          (!this.password && !this.passwordAutofill)) &&
        !this.isLoading
      );
    },
    keepMeLoggedInTooltip() {
      return this.cookiesAccepted
        ? this.$t("LoginPage.keepLoggedInTooltip")
        : this.$t("LoginPage.acceptCookiesToKeepLoginTooltip");
    },
  },
  mounted() {
    // Refresh the stay logged in checkbox
    this.setCookiesAccepted(areCookiesAccepted());
    this.stayLoggedIn = areCookiesAccepted()
      ? getLocalStorageStayLoggedIn()
      : false;

    // Fix for Chrome credential manager triggering the auto fill events without informing Vue every time.
    // This is why the code below is pure html5/js not using reference or anything related to the virtual DOM.
    // We must wait for it to trigger the auto-fill cancel and then the autofill-start for this to work, hence the timeout.
    this.loggingTimeoutIds = setTimeout(() => {
      const loginInput = document.getElementsByClassName(
        "companion-login-input"
      )[0];
      const loginElement = loginInput?.getElementsByTagName("input")[0];
      if (
        loginElement &&
        getComputedStyle(loginElement).animationName === "onAutoFillStart"
      ) {
        this.loginAutofill = true;
      }
      const passwordInput = document.getElementsByClassName(
        "companion-password-input"
      )[0];
      const passwordElement = passwordInput?.getElementsByTagName("input")[0];
      if (
        passwordElement &&
        getComputedStyle(passwordElement).animationName === "onAutoFillStart"
      ) {
        this.passwordAutofill = true;
      }
    }, 1000);
  },
  beforeDestroy() {
    // Clear timeout triggers that otherwise stay on the window worker
    clearTimeout(this.loggingTimeoutIds);
  },
  methods: {
    ...mapActions("Auth", ["setCookiesAccepted"]),
    async doLogin() {
      this.isLoading = true;
      this.loginAttemptsCount++;
      await login(this.login, this.password)
        .then(() => {
          updateUserAccessRights();
          this.$emit("logged");
        })
        .catch((error) => {
          let errorMessage = getErrorMessageFrom(
            error,
            this.$t("errors.login.loginError"),
            true
          );
          let warningMessage = "";

          const errors = error?.response?.data?.errors;
          const hasError = (key) =>
            Array.isArray(errors) && errors.includes(key);

          // notify about lockout error
          const lockoutErrorKey = "errors.login.lockedOut";
          if (hasError(lockoutErrorKey)) {
            errorMessage = this.$t("LoginPage.lockout", {
              lockOutMinutes: this.lockoutOptions.defaultLockoutTimeSpan,
            });
          } else if (
            this.lockoutOptions?.enabled &&
            this.loginAttemptsCount >=
              this.lockoutOptions.maxFailedAccessAttempts - 2
          ) {
            warningMessage = this.$t("LoginPage.accessAttemptsLeft", {
              maxLoginAttempts: this.lockoutOptions.maxFailedAccessAttempts,
              lockOutMinutes: this.lockoutOptions.defaultLockoutTimeSpan,
            });
          }
          // provide account deactivation information
          const isDeactivatedAccount =
            hasError("errors.login.accountArchived") ||
            hasError("errors.login.accountDeactivated");
          this.$emit(
            "loginError",
            errorMessage,
            warningMessage,
            isDeactivatedAccount
          );
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    // If the onAutofillStart animation started we want to set usernameAutofill to true
    loginAnimationStart(event) {
      if (event.animationName === "onAutoFillStart") {
        this.loginAutofill = true;
      }
    },
    // If the onAutofillStart animation started we want to set passwordAutofill to true
    passwordAnimationStart(event) {
      if (event.animationName === "onAutoFillStart") {
        this.passwordAutofill = true;
      }
    },
    updateLoginInputErrors() {
      this.loginErrors = [];
      if (!this.$v.login.login || !this.$v.login.required) {
        this.loginErrors.push(this.$t("errors.login.requireValidLogin"));
      }
    },
    onChangeStayLoggedIn() {
      this.stayLoggedIn = !this.stayLoggedIn;
      setLocalStorageStayLoggedIn(this.stayLoggedIn);
    },
    resetLoginInputErrors() {
      this.loginErrors = [];
    },
  },
  watch: {
    login() {
      this.loginAutofill = false;
    },
    password() {
      this.passwordAutofill = false;
    },
  },
};
</script>

<style>
/*
  Fix for Chrome autofill issues, see: https://rojas.io/how-to-detect-chrome-autofill-with-vue-js/
  TL;DR: Adding empty animation events permit to get animation events in Vue more reliably.
  This style must unfortunately be declared as non scoped.
 */
input:-webkit-autofill {
  animation-name: onAutoFillStart;
}
input:not(:-webkit-autofill) {
  animation-name: onAutoFillCancel;
}
@keyframes onAutoFillStart {
  from {
  }
  to {
  }
}
@keyframes onAutoFillCancel {
  from {
  }
  to {
  }
}
</style>
